import { stripArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  calculateRequiredCoverage,
  consequenceOfFailureSchema,
  type CoverageCalculation,
  type CoverageZone,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  InspectionStrategy,
  niiStrategySchema,
  rbaInformationSchema,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  ZONE_FIELD_IDS,
  zoneParamsSchema,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { ZoneCoverageBinding } from 'features/libra/stepComponents/zoneAnalysis/zoneCoverageBindings'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'

const strategyInputsSchema = niiStrategySchema
  .pick({
    strategy_type_calculated: true,
    strategy_type_override: true,
    degradation_likely: true,
    degradation_predictable: true,
    degradation_severity: true,
  })
  .optional()
const typeASchema = rbaInformationSchema
  .pick({
    density_of_degradation: true,
  })
  .default({})
const typeBSchema = rbaInformationSchema
  .pick({
    density_of_degradation: true,
    measurement_accuracy: true,
    tolerance_of_degradation: true,
    spatial_homogeneity: true,
  })
  .default({})

type ZoneRow = Record<string, unknown>

/** Persist current coverage for eligible zones in a merged ticket snapshot. */
export function recalculateZoneCoverage(
  previous: FieldValues,
  next: FieldValues,
  bindings: ReadonlyArray<ZoneCoverageBinding>
): FieldValues {
  let fieldValues = next

  for (const binding of bindings) {
    fieldValues = persistCollection(previous, fieldValues, binding)
  }

  return fieldValues
}

function persistCollection(
  previous: FieldValues,
  next: FieldValues,
  binding: ZoneCoverageBinding
): FieldValues {
  const { zoneFieldName } = binding
  const previousWrapper = previous[zoneFieldName]
  const nextWrapper = next[zoneFieldName]
  if (!nextWrapper) return next

  const nextRows = nextWrapper.value
  if (nextRows === null || nextRows === undefined) return next
  if (!Array.isArray(nextRows))
    throw new Error(`Invalid zone collection in field ${zoneFieldName}`)

  const ids = new Set<string>()
  const rows = nextRows.map((row) => {
    if (!isRecord(row))
      throw new Error(`Invalid zone row in field ${zoneFieldName}`)
    const id = row.id
    if (id !== undefined) {
      if (typeof id !== 'string' || id.length === 0 || ids.has(id))
        throw new Error(
          `Zone IDs must be unique and stable in ${zoneFieldName}`
        )
      ids.add(id)
    }
    return persistZoneCoverage(row, next, binding)
  })

  const wrapper: FieldValue = { ...nextWrapper, value: rows }
  const withOrigin =
    previousWrapper && sameZoneContent(previousWrapper.value, nextRows)
      ? restoreOrigin(previousWrapper, wrapper)
      : wrapper
  return { ...next, [zoneFieldName]: withOrigin }
}

function persistZoneCoverage(
  row: ZoneRow,
  fieldValues: FieldValues,
  binding: ZoneCoverageBinding
): ZoneRow {
  if (row.ai_suggested === true) return row
  if (binding.error) throw new Error(binding.error)

  const parsedStrategy = strategyInputsSchema.parse(row.nii_strategy)
  const strategy = getEffectiveStrategyType(parsedStrategy)
  if (!strategy) return replaceCoverageCalculation(row)

  const areaSqM = zoneParamsSchema.shape.total_surface_area_sq_m
    .unwrap()
    .parse(getEffectiveZoneArea(row))
  const cof = consequenceOfFailureSchema.parse(
    unwrapFieldValue<unknown>(fieldValues[ZONE_FIELD_IDS.COF]) ?? {}
  )
  const rawConfidence = binding.confidenceFieldName
    ? unwrapFieldValue<unknown>(fieldValues[binding.confidenceFieldName])
    : undefined
  const confidence =
    strategy === InspectionStrategy.C
      ? 'med'
      : niiStrategySchema.shape.confidence
          .unwrap()
          .parse(rawConfidence ?? 'med')

  const rba =
    strategy === InspectionStrategy.A
      ? typeASchema.parse(row.rba_info)
      : strategy === InspectionStrategy.B
        ? typeBSchema.parse(row.rba_info)
        : undefined
  const zone: CoverageZone = {
    total_surface_area_sq_m_override: areaSqM,
    nii_strategy: parsedStrategy,
    rba_info: rba,
  }
  return replaceCoverageCalculation(
    row,
    calculateRequiredCoverage(zone, getCofModifier(cof.level), confidence)
  )
}

function replaceCoverageCalculation(
  row: ZoneRow,
  calculation?: CoverageCalculation
): ZoneRow {
  const next = { ...row }
  if (calculation === undefined) delete next.coverage_calculation
  else next.coverage_calculation = calculation
  return next
}

function sameZoneContent(previous: unknown, next: unknown): boolean {
  const withoutCalculation = (value: unknown) =>
    Array.isArray(value)
      ? value.map((row) =>
          isRecord(row) ? omit(row, 'coverage_calculation') : row
        )
      : value
  return isEqual(
    stripArrayRowIds(withoutCalculation(previous)),
    stripArrayRowIds(withoutCalculation(next))
  )
}

function restoreOrigin(previous: FieldValue, next: FieldValue): FieldValue {
  const restored = omit(next, ['updateSource', 'updatedAt'])
  return {
    ...restored,
    ...(Object.hasOwn(previous, 'updateSource')
      ? { updateSource: previous.updateSource }
      : {}),
    ...(Object.hasOwn(previous, 'updatedAt')
      ? { updatedAt: previous.updatedAt }
      : {}),
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}
